import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LocationAutocompleteTransport = ({
  name,
  value,
  onChange,
  onLocationSelect,
  countryRestrictions = ["de", "be", "fr", "nl", "pl"],
  language = "en",
  minChars = 2,
  placeholder = null,
  disabled = false,
  className = "",
  wrapperClassName = ""  // ✅ جديد
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const { t } = useTranslation();

  const defaultPlaceholder = t("enter_address") || "Enter location...";
  const finalPlaceholder = placeholder || defaultPlaceholder;

  const buildUrl = (q) => {
    const base = "https://nominatim.openstreetmap.org/search";
    const params = new URLSearchParams({
      format: "json",
      addressdetails: "1",
      q,
      // countrycodes: countryRestrictions.join(","),
      "accept-language": language,
      limit: "8"
    });
    return `${base}?${params.toString()}`;
  };

  const fetchSuggestions = async (input) => {
    setFetchError("");
    setSuggestions([]);

    if (!input || input.length < minChars) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const url = buildUrl(input);
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const filtered = (data || [])
          .filter(p => p.address && (p.address.postcode || p.address.city || p.address.town || p.address.village))
          .map(p => {
            const postcode = p.address.postcode || "";
            const city = p.address.city || p.address.town || p.address.village || "";
            const countryCode = p.address.country_code ? p.address.country_code.toUpperCase() : "";
            const formatted = [postcode, city].filter(Boolean).join(" ") + (countryCode ? `, ${countryCode}` : "");
            return { ...p, formatted, lat: p.lat, lon: p.lon };
          });

        setSuggestions(filtered);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Autocomplete fetch error:", err);
          setFetchError(err.message.includes("Failed to fetch") 
            ? "Network error. Please check your connection." 
            : err.message || "Unknown error");
        }
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (place) => {
    if (!place) return;
    const formatted = place.formatted;
    const lat = place.lat;
    const lng = place.lon;

    if (onLocationSelect) onLocationSelect(name, formatted, lat, lng);

    if (onChange) {
      onChange({
        target: { name, value: formatted, lat, lng }
      });
    }

    setSuggestions([]);
    setFetchError("");
  };

  const handleOutsideClick = (e) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return (
    <div className={`autocomplete-wrapper location-autocomplete-wrapper ${wrapperClassName}`} ref={wrapperRef}>
      <input
        disabled={disabled}
        className={`form-control ${className}`}
        name={name}
        value={value || ""}
        placeholder={finalPlaceholder}
        onChange={(e) => {
          const val = e.target.value;
          if (onChange) onChange({ target: { name, value: val } });
          fetchSuggestions(val);
        }}
      />

      {loading && (
        <div style={{ position: "absolute", right: 10, top: 10, zIndex: 2100 }}>
          <small className="text-muted">Loading…</small>
        </div>
      )}

      {fetchError && (
        <div className="text-danger small mt-1">{fetchError}</div>
      )}

      {suggestions.length > 0 && (
        <ul className="autocomplete-dropdown">
          {suggestions.map((place, idx) => (
            <li
              key={idx}
              onClick={() => handleSelect(place)}
              className="autocomplete-item"
            >
              {place.formatted}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationAutocompleteTransport;